import {
  DoubleArrowLeftIcon,
  DoubleArrowRightIcon,
} from "@radix-ui/react-icons";
import type { PaginationState } from "@tanstack/react-table";

import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import { useMobile } from "@/hooks/use-mobile";
import { usePagination } from "@/hooks/use-pagination";

interface TablePaginationProps {
  loading: boolean;
  pagination: PaginationState;
  onPaginate: (pageIndex: number) => void;
  itemsPerPage: number;
  totalCount: number;
}

const TablePagination = ({
  loading,
  pagination,
  onPaginate,
  itemsPerPage,
  totalCount,
}: TablePaginationProps) => {
  const { isMobile } = useMobile();
  const totalPages = Math.ceil(totalCount / itemsPerPage);
  const currentPage = pagination.pageIndex + 1;

  const { pages, showLeftEllipsis, showRightEllipsis } = usePagination({
    currentPage,
    totalPages,
    paginationItemsToDisplay: isMobile ? 3 : 10,
  });

  return (
    <div className="my-4 flex flex-col gap-2">
      <div className="mb-2 text-center text-xs text-muted">
        showing page {currentPage} of {totalPages} from{" "}
        {totalCount.toLocaleString()} results
      </div>
      <Pagination>
        <PaginationContent>
          {!isMobile && (
            <PaginationItem>
              <PaginationLink
                className="
                  hidden p-1
                  aria-disabled:pointer-events-none aria-disabled:opacity-50
                  sm:flex
                "
                aria-disabled={currentPage === 1 || loading}
                onClick={() => onPaginate(0)}
                role={currentPage === 1 ? "link" : undefined}
              >
                <DoubleArrowLeftIcon />
              </PaginationLink>
            </PaginationItem>
          )}

          <PaginationItem>
            <PaginationPrevious
              className="
                aria-disabled:pointer-events-none aria-disabled:opacity-50
              "
              aria-disabled={currentPage === 1 || loading}
              onClick={() => currentPage > 1 && onPaginate(currentPage - 2)}
              role={currentPage === 1 ? "link" : undefined}
            />
          </PaginationItem>

          {showLeftEllipsis && (
            <PaginationItem>
              <PaginationEllipsis />
            </PaginationItem>
          )}

          {pages.map((page) => (
            <PaginationItem key={page}>
              <PaginationLink
                variant={page === currentPage ? "outlinePrimary" : "outline"}
                isActive={page === currentPage}
                onClick={() => onPaginate(page - 1)}
                disabled={loading}
              >
                {page}
              </PaginationLink>
            </PaginationItem>
          ))}

          {showRightEllipsis && (
            <PaginationItem>
              <PaginationEllipsis />
            </PaginationItem>
          )}

          <PaginationItem>
            <PaginationNext
              className="
                aria-disabled:pointer-events-none aria-disabled:opacity-50
              "
              aria-disabled={currentPage === totalPages || loading}
              onClick={() =>
                currentPage < totalPages && onPaginate(currentPage)
              }
              role={currentPage === totalPages ? "link" : undefined}
            />
          </PaginationItem>

          {!isMobile && (
            <PaginationItem>
              <PaginationLink
                className="
                  hidden p-1
                  aria-disabled:pointer-events-none aria-disabled:opacity-50
                  sm:flex
                "
                aria-disabled={currentPage === totalPages || loading}
                onClick={() => onPaginate(totalPages - 1)}
                role={currentPage === totalPages ? "link" : undefined}
              >
                <DoubleArrowRightIcon />
              </PaginationLink>
            </PaginationItem>
          )}
        </PaginationContent>
      </Pagination>
    </div>
  );
};

export default TablePagination;
